<template>
  <div class="rh-page">

    <!-- ─── Hero Header ─── -->
    <div class="rh-hero">
      <div class="rh-hero__inner">
        <div>
          <div class="rh-hero__title">ประวัติการทำงาน</div>
          <div class="rh-hero__sub">รายการออเดอร์และการเดินทางทั้งหมด</div>
        </div>
        <div class="rh-hero__icon">📋</div>
      </div>

      <!-- Summary Stats -->
      <div class="rh-stats-row">
        <div class="rh-stat-pill">
          <div class="rh-stat-pill__val">{{ summary.total }}</div>
          <div class="rh-stat-pill__lbl">รายการทั้งหมด</div>
        </div>
        <div class="rh-stat-divider"></div>
        <div class="rh-stat-pill">
          <div class="rh-stat-pill__val">฿{{ formatNum(summary.earnings) }}</div>
          <div class="rh-stat-pill__lbl">รายได้รวม</div>
        </div>
        <div class="rh-stat-divider"></div>
        <div class="rh-stat-pill">
          <div class="rh-stat-pill__val">{{ summary.orders }}</div>
          <div class="rh-stat-pill__lbl">ออเดอร์</div>
        </div>
        <div class="rh-stat-divider"></div>
        <div class="rh-stat-pill">
          <div class="rh-stat-pill__val">{{ summary.travels }}</div>
          <div class="rh-stat-pill__lbl">เรียกรถ</div>
        </div>
      </div>
    </div>

    <!-- ─── Filter Tabs ─── -->
    <div class="rh-body">
      <div class="rh-filter-row">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          class="rh-tab-btn"
          :class="{ 'rh-tab-btn--active': activeTab === tab.value }"
          @click="activeTab = tab.value"
        >
          <v-icon size="14" class="mr-1">{{ tab.icon }}</v-icon>
          {{ tab.label }}
        </button>
      </div>

      <!-- ─── Loading ─── -->
      <div v-if="loading" class="rh-loading-wrapper">
        <div v-for="n in 4" :key="n" class="rh-skeleton-card">
          <div class="rh-sk__top">
            <div class="rh-sk__badge"></div>
            <div class="rh-sk__num"></div>
          </div>
          <div class="rh-sk__line rh-sk__line--long"></div>
          <div class="rh-sk__line rh-sk__line--short"></div>
        </div>
      </div>

      <!-- ─── Empty state ─── -->
      <div v-else-if="filteredItems.length === 0" class="rh-empty">
        <div class="rh-empty__icon">🗂️</div>
        <div class="rh-empty__title">ยังไม่มีประวัติ</div>
        <div class="rh-empty__sub">รายการจะแสดงเมื่อคุณรับงานและจัดส่งสำเร็จ</div>
      </div>

      <!-- ─── History Cards ─── -->
      <div v-else>
        <!-- Date group -->
        <div v-for="group in groupedItems" :key="group.date">
          <div class="rh-date-header">
            <span class="rh-date-header__label">{{ group.dateLabel }}</span>
            <span class="rh-date-header__count">{{ group.items.length }} รายการ</span>
          </div>

          <div
            v-for="item in group.items"
            :key="item.type + '-' + item.id"
            class="rh-card"
            @click="goDetail(item)"
          >
            <!-- Left accent strip -->
            <div class="rh-card__strip" :class="getStripClass(item)"></div>

            <div class="rh-card__content">
              <!-- Top row: type badge + number + fee -->
              <div class="rh-card__top">
                <div class="rh-type-badge" :class="item.type === 'order' ? 'rh-type-badge--order' : 'rh-type-badge--travel'">
                  <v-icon size="12" color="inherit" class="mr-1">{{ item.type === 'order' ? 'mdi-store' : 'mdi-car-side' }}</v-icon>
                  {{ item.type === 'order' ? 'ออเดอร์' : 'เรียกรถ' }}
                </div>
                <span class="rh-card__num">#{{ item.refNo }}</span>
                <div class="rh-card__spacer"></div>
                <div class="rh-card__fee">฿{{ formatNum(item.fee) }}</div>
              </div>

              <!-- Route -->
              <div class="rh-card__route">
                <div class="rh-route-row">
                  <div class="rh-route-indicator">
                    <div class="rh-route-dot rh-route-dot--from"></div>
                    <div class="rh-route-connector"></div>
                  </div>
                  <div class="rh-route-info">
                    <div class="rh-route-lbl">{{ item.type === 'order' ? 'ร้านค้า' : 'จุดรับ' }}</div>
                    <div class="rh-route-name">{{ item.from }}</div>
                    <div v-if="item.fromDetail" class="rh-route-note">
                      <v-icon size="10" color="#16a34a" class="mr-1">mdi-note-text-outline</v-icon>
                      {{ item.fromDetail }}
                    </div>
                  </div>
                </div>
                <div class="rh-route-row">
                  <div class="rh-route-indicator">
                    <div class="rh-route-dot rh-route-dot--to"></div>
                  </div>
                  <div class="rh-route-info">
                    <div class="rh-route-lbl">{{ item.type === 'order' ? 'ลูกค้า' : 'จุดส่ง' }}</div>
                    <div class="rh-route-name">{{ item.to }}</div>
                    <div v-if="item.toDetail" class="rh-route-note">
                      <v-icon size="10" color="#dc2626" class="mr-1">mdi-note-text-outline</v-icon>
                      {{ item.toDetail }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Vehicle type + penalty badges (travel only) -->
              <div v-if="item.type === 'travel'" class="rh-card__tags">
                <span v-if="item.vehicleTypeName" class="rh-tag rh-tag--vehicle">
                  <v-icon size="10" class="mr-1">mdi-car-side</v-icon>{{ item.vehicleTypeName }}
                </span>
                <span v-if="item.hasPenalty && !item.penaltyReleased" class="rh-tag rh-tag--penalty">
                  🔒 ถูกพักเงิน
                </span>
                <span v-if="item.hasPenalty && item.penaltyReleased" class="rh-tag rh-tag--released">
                  ✅ คืนแล้ว
                </span>
                <span v-if="item.rating" class="rh-tag rh-tag--rating">
                  ⭐ {{ item.rating }}.0
                </span>
              </div>

              <!-- Footer: status + time -->
              <div class="rh-card__footer">
                <div class="rh-status-chip" :class="getStatusChipClass(item.status)">
                  <div class="rh-status-chip__dot"></div>
                  {{ getStatusLabel(item.status, item.type) }}
                </div>
                <div class="rh-card__time">
                  <v-icon size="12" color="#9CA3AF" class="mr-1">mdi-clock-outline</v-icon>
                  {{ formatTime(item.date) }}
                </div>
              </div>
            </div>

            <v-icon size="18" color="#D1D5DB" class="rh-card__chevron">mdi-chevron-right</v-icon>
          </div>
        </div>

        <!-- Load more / pagination info -->
        <div class="rh-page-info">
          แสดง {{ filteredItems.length }} รายการ
        </div>
      </div>

      <div style="height: 80px;"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: "RiderHistory",

  data() {
    return {
      loading: false,
      activeTab: "all",
      tabs: [
        { value: "all",    label: "ทั้งหมด",  icon: "mdi-view-list" },
        { value: "order",  label: "ออเดอร์",  icon: "mdi-store" },
        { value: "travel", label: "เรียกรถ",  icon: "mdi-car-side" },
      ],
      items: [],   // combined history
    };
  },

  computed: {
    filteredItems() {
      if (this.activeTab === "all") return this.items;
      return this.items.filter(i => i.type === this.activeTab);
    },

    groupedItems() {
      const groups = {};
      this.filteredItems.forEach(item => {
        const d = new Date(item.date);
        const key = d.toLocaleDateString("th-TH", { year: "numeric", month: "long", day: "numeric" });
        if (!groups[key]) groups[key] = { date: key, dateLabel: key, items: [] };
        groups[key].items.push(item);
      });
      return Object.values(groups).sort((a, b) => {
        const da = new Date(a.items[0].date);
        const db = new Date(b.items[0].date);
        return db - da;
      });
    },

    summary() {
      const orders  = this.items.filter(i => i.type === "order");
      const travels = this.items.filter(i => i.type === "travel");
      const earnings = this.items.reduce((s, i) => s + (i.fee || 0), 0);
      return {
        total: this.items.length,
        orders: orders.length,
        travels: travels.length,
        earnings,
      };
    },
  },

  mounted() {
    this.loadHistory();
  },

  methods: {
    async loadHistory() {
      this.loading = true;
      try {
        const headers = { Authorization: `Bearer ${this.$store.getters.accessToken}` };

        // ── Fetch orders + travels in parallel ──
        const [orderRes, travelRes] = await Promise.allSettled([
          this.$axios.get(`${this.$domain_api}/api/v1/auth/rider/orders`, { headers }),
          this.$axios.get(`${this.$domain_api}/api/v1/auth/rider/travels`, { headers }),
        ]);

        let orderItems = [];
        let travelItems = [];

        // ── Parse orders (GET /rider/orders returns { success, data: [] }) ──
        if (orderRes.status === "fulfilled") {
          const raw = orderRes.value.data;
          const orders = Array.isArray(raw) ? raw
                       : Array.isArray(raw?.data) ? raw.data
                       : [];
          orderItems = orders.map(o => ({
            id: o.id,
            type: "order",
            refNo: o.orderNumber || String(o.id).slice(-6).toUpperCase(),
            from: o.seller?.shopName || "ไม่ระบุร้านค้า",
            to: `${o.buyer?.firstName || ""} ${o.buyer?.lastName || ""}`.trim()
                || o.address?.firstName
                || "ลูกค้า",
            fee: o.shipFee || 0,
            status: o.status,
            date: o.updatedAt || o.createdAt,
          }));
        } else {
          console.warn("[RiderHistory] orders fetch failed:", orderRes.reason?.message);
        }

        // ── Parse travels (GET /rider/travels returns { success, data: [] }) ──
        if (travelRes.status === "fulfilled") {
          const raw = travelRes.value.data;
          const travels = Array.isArray(raw?.data) ? raw.data
                        : Array.isArray(raw) ? raw
                        : [];
          travelItems = travels.map(t => ({
            id: t.id,
            type: "travel",
            refNo: t.travelNumber || `TRIP-${String(t.id).slice(-6).toUpperCase()}`,
            from: t.addressSnapshotNow || "ไม่ระบุจุดรับ",
            to: t.addressSnapshotStart || "ไม่ระบุจุดส่ง",
            fromDetail: t.originDetail || "",
            toDetail: t.destinationDetail || "",
            fee: t.price || 0,
            status: t.status,
            date: t.updatedAt || t.createdAt,
            vehicleTypeName: t.vehicleType?.name || null,
            hasPenalty: !!t.riderPenalty,
            penaltyReleased: t.riderPenalty?.status === "released",
            rating: t.riderReview?.rating || null,
          }));
        } else {
          console.warn("[RiderHistory] travels fetch failed:", travelRes.reason?.message);
        }

        // ── Merge + sort newest first ──
        this.items = [...orderItems, ...travelItems].sort((a, b) => {
          return new Date(b.date) - new Date(a.date);
        });

      } catch (err) {
        console.error("[RiderHistory] load error:", err);
        this.$swal.fire({ icon: "error", title: "ข้อผิดพลาด", text: "ไม่สามารถโหลดประวัติได้" });
      } finally {
        this.loading = false;
      }
    },

    formatNum(n) {
      return new Intl.NumberFormat("th-TH").format(n || 0);
    },

    formatTime(d) {
      if (!d) return "—";
      return new Date(d).toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" });
    },

    getStatusLabel(status, type) {
      if (type === "travel") {
        return { rider_accepted: "ไปรับผู้โดยสาร", in_progress: "กำลังเดินทาง", completed: "สำเร็จ", cancelled: "ยกเลิก" }[status] || status;
      }
      return {
        pending: "รอรับงาน",
        accepted: "รับงานแล้ว",
        processing: "รอรับสินค้า",
        picked_up: "รับสินค้าแล้ว",
        delivering: "กำลังจัดส่ง",
        delivered: "จัดส่งสำเร็จ",
        cancelled: "ยกเลิก",
      }[status] || status;
    },

    getStatusChipClass(status) {
      if (["delivered", "completed"].includes(status)) return "rh-status-chip--success";
      if (["cancelled"].includes(status)) return "rh-status-chip--error";
      if (["delivering", "in_progress", "picked_up"].includes(status)) return "rh-status-chip--info";
      return "rh-status-chip--default";
    },

    getStripClass(item) {
      if (item.type === "travel") return "rh-card__strip--travel";
      if (["delivered", "completed"].includes(item.status)) return "rh-card__strip--success";
      if (["cancelled"].includes(item.status)) return "rh-card__strip--error";
      return "rh-card__strip--primary";
    },

    goDetail(item) {
      if (item.type === "travel") {
        this.$router.push({ name: "rider_travel_detail", params: { id: item.id } });
      } else {
        this.$router.push({ name: "rider_order_detail", params: { id: item.id } });
      }
    },
  },
};
</script>

<style scoped>
/* ── Fonts ── */
.rh-page {
  min-height: 100vh;
  background: #F1F5F9;
  font-family: 'Sarabun', 'Noto Sans Thai', sans-serif;
  overflow-x: hidden;
}

/* ── Hero ── */
.rh-hero {
  background: linear-gradient(135deg, #1E3A8A 0%, #1D4ED8 60%, #2563EB 100%);
  padding: 24px 20px 0;
  border-radius: 0 0 32px 32px;
}

.rh-hero__inner {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
}

.rh-hero__title {
  font-size: 22px;
  font-weight: 800;
  color: white;
  letter-spacing: -0.5px;
}

.rh-hero__sub {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.65);
  margin-top: 3px;
}

.rh-hero__icon {
  font-size: 32px;
  line-height: 1;
}

/* Stats Row */
.rh-stats-row {
  display: flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.15);
  border-radius: 20px 20px 0 0;
  padding: 14px 16px;
  gap: 0;
}

.rh-stat-pill {
  flex: 1;
  text-align: center;
}

.rh-stat-pill__val {
  font-size: 15px;
  font-weight: 800;
  color: white;
}

.rh-stat-pill__lbl {
  font-size: 10px;
  color: rgba(255,255,255, 0.65);
  margin-top: 2px;
}

.rh-stat-divider {
  width: 1px;
  height: 28px;
  background: rgba(255,255,255,0.2);
}

/* ── Body ── */
.rh-body {
  padding: 16px;
}

/* ── Filter Tabs ── */
.rh-filter-row {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.rh-tab-btn {
  display: flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Sarabun', 'Noto Sans Thai', sans-serif;
  border: 1.5px solid #E2E8F0;
  background: white;
  color: #6B7280;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.rh-tab-btn:active { transform: scale(0.96); }

.rh-tab-btn--active {
  background: #1D4ED8;
  border-color: #1D4ED8;
  color: white;
  box-shadow: 0 4px 12px rgba(29, 78, 216, 0.3);
}

/* ── Loading skeleton ── */
.rh-loading-wrapper { display: flex; flex-direction: column; gap: 12px; }

.rh-skeleton-card {
  background: white;
  border-radius: 18px;
  padding: 16px;
  animation: rh-shimmer 1.5s infinite;
}

.rh-sk__top { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
.rh-sk__badge { width: 60px; height: 22px; background: #E2E8F0; border-radius: 10px; }
.rh-sk__num { width: 80px; height: 14px; background: #E2E8F0; border-radius: 6px; }
.rh-sk__line { height: 12px; background: #E2E8F0; border-radius: 6px; margin-top: 10px; }
.rh-sk__line--long { width: 80%; }
.rh-sk__line--short { width: 55%; }

@keyframes rh-shimmer {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.55; }
}

/* ── Empty State ── */
.rh-empty {
  text-align: center;
  padding: 60px 20px;
}

.rh-empty__icon { font-size: 52px; margin-bottom: 16px; }
.rh-empty__title { font-size: 17px; font-weight: 700; color: #374151; margin-bottom: 8px; }
.rh-empty__sub { font-size: 13px; color: #9CA3AF; }

/* ── Date Group ── */
.rh-date-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 20px 0 10px;
}

.rh-date-header__label {
  font-size: 13px;
  font-weight: 700;
  color: #374151;
}

.rh-date-header__count {
  font-size: 11px;
  color: #9CA3AF;
  background: white;
  padding: 2px 10px;
  border-radius: 10px;
  border: 1px solid #E2E8F0;
}

/* ── History Card ── */
.rh-card {
  background: white;
  border-radius: 18px;
  margin-bottom: 12px;
  display: flex;
  align-items: stretch;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
  position: relative;
}

.rh-card:active {
  transform: scale(0.98);
  box-shadow: 0 1px 6px rgba(0,0,0,0.08);
}

.rh-card__strip {
  width: 5px;
  flex-shrink: 0;
}

.rh-card__strip--primary  { background: linear-gradient(180deg, #F4811F, #E56A0A); }
.rh-card__strip--travel   { background: linear-gradient(180deg, #1D4ED8, #2563EB); }
.rh-card__strip--success  { background: linear-gradient(180deg, #10B981, #059669); }
.rh-card__strip--error    { background: linear-gradient(180deg, #EF4444, #DC2626); }

.rh-card__content {
  flex: 1;
  padding: 14px 12px 14px 16px;
  min-width: 0;
}

.rh-card__chevron {
  align-self: center;
  padding-right: 12px;
  flex-shrink: 0;
}

/* Top row */
.rh-card__top {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.rh-type-badge {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 10px;
  white-space: nowrap;
}

.rh-type-badge--order {
  background: #FFF7ED;
  color: #C2410C;
}

.rh-type-badge--travel {
  background: #EFF6FF;
  color: #1D4ED8;
}

.rh-card__num {
  font-size: 12px;
  font-weight: 700;
  color: #374151;
}

.rh-card__spacer { flex: 1; }

.rh-card__fee {
  font-size: 16px;
  font-weight: 800;
  color: #10B981;
}

/* Route */
.rh-card__route {
  margin-bottom: 12px;
}

.rh-route-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.rh-route-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  padding-top: 4px;
}

.rh-route-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.rh-route-dot--from {
  background: #10B981;
  box-shadow: 0 0 0 3px rgba(16,185,129,0.15);
}

.rh-route-dot--to {
  background: #EF4444;
  box-shadow: 0 0 0 3px rgba(239,68,68,0.12);
}

.rh-route-connector {
  width: 1.5px;
  height: 20px;
  background: #E2E8F0;
  margin: 3px 0;
}

.rh-route-info {
  flex: 1;
  min-width: 0;
  padding-bottom: 10px;
}

.rh-route-lbl {
  font-size: 10px;
  font-weight: 600;
  color: #9CA3AF;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 1px;
}

.rh-route-name {
  font-size: 13px;
  font-weight: 600;
  color: #1F2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rh-route-note {
  display: flex;
  align-items: flex-start;
  margin-top: 3px;
  padding: 3px 6px;
  background: #fffbeb;
  border-left: 2px solid #f59e0b;
  border-radius: 3px;
  font-size: 11px;
  color: #78350f;
  word-break: break-word;
  line-height: 1.4;
}

/* Footer */
.rh-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.rh-status-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 10px;
}

.rh-status-chip__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.rh-status-chip--success { background: #ECFDF5; color: #065F46; }
.rh-status-chip--error   { background: #FEF2F2; color: #991B1B; }
.rh-status-chip--info    { background: #EFF6FF; color: #1D4ED8; }
.rh-status-chip--default { background: #F3F4F6; color: #6B7280; }

.rh-card__time {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #9CA3AF;
}

/* ── Tags row ── */
.rh-card__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.rh-tag {
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 8px;
}

.rh-tag--vehicle  { background: #EFF6FF; color: #1D4ED8; }
.rh-tag--penalty  { background: #FEF2F2; color: #991B1B; }
.rh-tag--released { background: #ECFDF5; color: #065F46; }
.rh-tag--rating   { background: #FFFBEB; color: #B45309; }

/* ── Page Info ── */
.rh-page-info {
  text-align: center;
  font-size: 12px;
  color: #9CA3AF;
  padding: 16px;
}
</style>
